import React, { useState, useEffect } from 'react';
import { apiCall } from '../lib/api.js';
import { CATEGORIES, RATING_OPTIONS, calculateScore } from '../lib/scorecard-config.js';

const ATTR_MAP = {};
CATEGORIES.forEach(cat => cat.attributes.forEach(a => { ATTR_MAP[a.id] = a.label; }));

const STATUS_STYLES = {
  pending: { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Pending' },
  accepted: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Accepted' },
  rejected: { bg: 'bg-red-50 text-red-700 border-red-200', label: 'Rejected' },
};
const BAND_STYLES = { 'Excellent': 'bg-emerald-50 text-emerald-700', 'Good': 'bg-blue-50 text-blue-700', 'Okay': 'bg-amber-50 text-amber-700', 'Needs improvement': 'bg-orange-50 text-orange-700', 'Failed': 'bg-red-50 text-red-700' };

export default function AppealsPage() {
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescoringId, setRescoringId] = useState(null);
  const [newScores, setNewScores] = useState({});
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchAppeals = async () => {
    try { const d = await apiCall('/appeals'); setAppeals(d.appeals || []); } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetchAppeals(); }, []);

  const handleAccept = async (appeal) => {
    try {
      // Calculate what the new final score would be
      // We pass both old and new info so the Lambda can show it in the email
      await apiCall('/appeal/resolve', { method: 'POST', body: {
        ...appeal, appeal_id: appeal.id, status: 'accepted', new_scores: newScores,
        new_final_score: null, new_rating_band: null,
      } });
      setRescoringId(null); setNewScores({});
      fetchAppeals();
    } catch (e) { alert(e.message); }
  };

  const handleReject = async (appeal) => {
    if (!rejectReason.trim()) { alert('Please enter a reason for rejection'); return; }
    try {
      await apiCall('/appeal/resolve', { method: 'POST', body: { ...appeal, appeal_id: appeal.id, status: 'rejected', reject_reason: rejectReason } });
      setRejectingId(null); setRejectReason('');
      fetchAppeals();
    } catch (e) { alert(e.message); }
  };

  const pending = appeals.filter(a => a.status === 'pending');
  const resolved = appeals.filter(a => a.status !== 'pending');

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div>
      <h2 className="text-lg font-medium mb-5">Appeals</h2>

      {appeals.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <i className="ti ti-message-circle text-3xl text-gray-300" aria-hidden="true"></i>
          <p className="text-gray-400 mt-2 text-sm">No appeals yet.</p>
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-amber-700 flex items-center gap-2 mb-3">
                <i className="ti ti-clock" aria-hidden="true"></i> Pending review ({pending.length})
              </h3>
              <div className="space-y-3">
                {pending.map(appeal => (
                  <div key={appeal.id} className="bg-white rounded-xl border border-amber-200 overflow-hidden">
                    <div className="p-5">
                      <div className="flex flex-wrap items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                          {appeal.agent_name?.split(' ').map(w => w[0]).join('').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{appeal.agent_name}</div>
                          <div className="text-xs text-gray-400 mt-0.5">#{appeal.ticket_number} &middot; {appeal.ticket_subject}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{Math.round((appeal.original_score || 0) * 100)}%</span>
                          <span className={'text-[11px] font-medium px-2 py-0.5 rounded-full ' + (BAND_STYLES[appeal.rating_band] || '')}>{appeal.rating_band}</span>
                        </div>
                      </div>

                      {/* Appealed attributes */}
                      {appeal.appealed_attributes?.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {appeal.appealed_attributes.map((attr, i) => (
                            <div key={i} className="bg-amber-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-medium text-amber-800">{attr.id}: {ATTR_MAP[attr.id] || attr.id}</span>
                                <span className="text-[11px] text-amber-600">Current: {attr.current_rating}</span>
                              </div>
                              <p className="text-sm text-gray-700">{attr.reason}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="text-xs text-gray-400 mb-3">
                        Submitted {new Date(appeal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>

                      {/* RESCORING PANEL */}
                      {rescoringId === appeal.id ? (
                        <div className="border border-emerald-200 rounded-lg p-4 bg-emerald-50 mt-3">
                          <h4 className="text-sm font-medium text-emerald-800 mb-3">Re-score appealed attributes</h4>
                          {(appeal.appealed_attributes || []).map((attr, i) => (
                            <div key={i} className="flex items-center justify-between py-2 border-b border-emerald-100 last:border-0">
                              <span className="text-sm text-gray-700">{ATTR_MAP[attr.id] || attr.id}</span>
                              <div className="flex gap-1">
                                {RATING_OPTIONS.map(opt => (
                                  <button key={opt} onClick={() => setNewScores(p => ({ ...p, [attr.id]: opt }))}
                                    className={'px-2.5 py-1 rounded-full text-[11px] font-medium border transition '
                                      + ((newScores[attr.id] || attr.current_rating) === opt
                                        ? opt === 'Meets' ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                        : opt === 'Partial' ? 'bg-amber-100 text-amber-700 border-amber-300'
                                        : opt === 'Misses' ? 'bg-red-100 text-red-700 border-red-300'
                                        : 'bg-gray-200 text-gray-600 border-gray-300'
                                        : 'border-gray-200 text-gray-400')}>
                                    {opt}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                          <div className="flex gap-2 mt-3">
                            <button onClick={() => handleAccept(appeal)}
                              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-xs font-medium">
                              Confirm re-score
                            </button>
                            <button onClick={() => { setRescoringId(null); setNewScores({}); }}
                              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">Cancel</button>
                          </div>
                        </div>
                      ) : rejectingId === appeal.id ? (
                        <div className="border border-red-200 rounded-lg p-4 bg-red-50 mt-3">
                          <h4 className="text-sm font-medium text-red-800 mb-2">Reason for rejection</h4>
                          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2}
                            placeholder="Explain why the appeal is being rejected..."
                            className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-red-300 resize-none mb-3" />
                          <div className="flex gap-2">
                            <button onClick={() => handleReject(appeal)}
                              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium">
                              Confirm rejection
                            </button>
                            <button onClick={() => { setRejectingId(null); setRejectReason(''); }}
                              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => { setRescoringId(appeal.id); const init = {}; (appeal.appealed_attributes || []).forEach(a => { init[a.id] = a.current_rating; }); setNewScores(init); }}
                            className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 text-xs font-medium flex items-center gap-1">
                            <i className="ti ti-check text-sm" aria-hidden="true"></i> Accept & re-score
                          </button>
                          <button onClick={() => setRejectingId(appeal.id)}
                            className="px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 text-xs font-medium flex items-center gap-1">
                            <i className="ti ti-x text-sm" aria-hidden="true"></i> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {resolved.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2 mb-3">
                <i className="ti ti-checks" aria-hidden="true"></i> Resolved ({resolved.length})
              </h3>
              <div className="space-y-2">
                {resolved.map(appeal => {
                  const st = STATUS_STYLES[appeal.status] || STATUS_STYLES.pending;
                  return (
                    <div key={appeal.id} className="bg-white rounded-xl border border-gray-200 px-5 py-3 flex items-center gap-4 opacity-80">
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-medium">
                        {appeal.agent_name?.split(' ').map(w => w[0]).join('').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{appeal.agent_name} <span className="font-normal text-gray-400">— #{appeal.ticket_number}</span></div>
                        {appeal.reject_reason && <div className="text-xs text-gray-400 truncate">Reason: {appeal.reject_reason}</div>}
                      </div>
                      <span className={'text-[11px] font-medium px-2.5 py-1 rounded-full border ' + st.bg}>{st.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
